import throttle from 'lodash/throttle';
import React, { useEffect, useState } from 'react';

import { SeasonalGuideSection } from './SeasonalGuideSection';
import { SeasonalGuideSectionsNav } from './SeasonalGuideSectionsNav';
import { TSeasonalGuideSection } from './types';

type TSeasonalGuideSectionsProps = {
  sections: TSeasonalGuideSection[];
};

export const SeasonalGuideSections = ({ sections }: TSeasonalGuideSectionsProps) => {
  const [activeSectionId, setActiveSectionId] = useState<string>();

  useEffect(() => {
    const determineActiveSection = throttle(
      () => {
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const visibleSections: Array<{ sectionId: string; visible: number }> = [];

        document.querySelectorAll('[data-seasonal-guide-section]').forEach(section => {
          const { top, bottom } = section.getBoundingClientRect();

          if (windowHeight - top > 0 && bottom > 0) {
            visibleSections.push({
              sectionId: section.getAttribute('id') || '',
              visible: top < 0 ? bottom : windowHeight - top,
            });
          }
        });

        visibleSections.sort((a, b) => b.visible - a.visible);

        setActiveSectionId(visibleSections[0]?.sectionId);
      },
      100,
      {
        leading: true,
        trailing: true,
      },
    );

    determineActiveSection();

    window.addEventListener('scroll', determineActiveSection, { passive: true });
    window.addEventListener('resize', determineActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', determineActiveSection);
      window.removeEventListener('resize', determineActiveSection);
    };
  }, [sections]);

  return (
    <>
      <SeasonalGuideSectionsNav
        sections={sections}
        activeSectionId={activeSectionId || sections[0]?.id}
        onScrollToSection={(sectionId: string) => {
          const element = document.getElementById(sectionId);

          if (element) {
            setTimeout(() => {
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.scrollY - 72;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });
            }, 100);
          }
        }}
      />

      {sections.map((section, index) => (
        <SeasonalGuideSection key={index} {...section} />
      ))}
    </>
  );
};
